import { useState, type FormEvent } from "react";
import { TotpCodeInput } from "./TotpCodeInput";
import { TotpQr } from "./TotpQr";
import { errorMessage } from "./errorMessage";

export interface TotpSetup {
  secret: string;
  uri: string;
}

export interface TotpEnrollProps {
  // 지금 등록돼 있나(계정의 totp_enabled).
  enabled: boolean;
  // 정책이 이 계정에 등록을 요구하나(totp_required). 요구되면 본인 해제 단추를 안 그린다 —
  // 서버도 거절한다(관리자 해제만).
  required: boolean;
  // 등록 1단계: 비밀번호 재확인 → 비밀과 otpauth URI. 서버는 아직 저장하지 않는다.
  onSetup: (password: string) => Promise<TotpSetup>;
  // 등록 2단계: 앱 코드 확인 → 서버가 그때 저장한다.
  onConfirm: (code: string) => Promise<void>;
  // 본인 해제: 비밀번호 재확인.
  onDisable: (password: string) => Promise<void>;
  // 해제 직전 확인. 주면 해제 단추를 눌렀을 때 먼저 부르고, false(또는 false 로 풀리는 Promise)면 멈춘다 —
  // 부르는 쪽이 자기 모달(빨간 테두리 확인 창 등)로 묻게 하려는 자리다. 비밀번호는 지우지 않아 다시 누르면 된다.
  // 안 주면 지금처럼 바로 해제한다(안 깨는 추가, mops-code#97~104).
  confirmDisable?: (message: string) => boolean | Promise<boolean>;
  // 등록·해제가 끝난 뒤 부르는 쪽이 할 일(이동 등). 안 주면 안내 문장만 보인다.
  onEnrolled?: () => void;
  onDisabled?: () => void;
  // 발급자 안내에 쓸 앱 이름 예시. 서비스가 바꿀 일은 거의 없다.
  appsHint?: string;
  // MOPS 자체 OTP 앱(공통 react/otp-app/v1)이 서빙되는 주소(예: "/otp/" 또는 "https://hub…/otp/").
  // 주면 QR 단계에 「MOPS OTP 앱에 추가」 링크가 생긴다 — 휴대폰 브라우저로 이 화면을 열어
  // QR 을 자기 화면으로 못 찍는 사람의 길이다. 없으면 아무것도 안 그린다(기존 소비자 그대로).
  otpAppUrl?: string;
  // 비밀번호가 없는 계정(SSO 로만 들어오는 계정 등)이면 true. 등록 시작·해제에서 비밀번호 칸을 그리지 않고
  // onSetup·onDisable 에 빈 문자열을 넘긴다 — 본인 확인은 서버가 다른 길(최근 로그인 등)로 한다.
  // 안 주면 지금처럼 비밀번호를 받는다(안 깨는 추가, tiki-mops 가 처음 쓴다).
  passwordless?: boolean;
  // passwordless 일 때 등록 시작 앞에 보일 본인 확인 안내. 서버가 쓰는 길이 서비스마다 달라 바꿀 자리를 둔다.
  passwordlessNote?: string;
}

// 해제 확인 창에 넘기는 문장 — 무엇이 사라지는지를 말한다. 부르는 쪽 모달이 그대로 쓰거나 자기 문장으로 바꾼다.
export const DISABLE_CONFIRM = "2단계 인증을 해제하시겠습니까? 다음 로그인부터 비밀번호만 묻습니다. 다시 쓰려면 인증 앱을 새로 등록해야 합니다.";

// 딥링크 주소. otp-app 은 `?add=<otpauth URI>` 를 읽고 주소줄에서 지운다. URI 안의 `&`·`?` 가
// 쿼리를 가르지 않도록 통째로 encodeURIComponent 한다 — 읽는 쪽(URLSearchParams)이 되돌린다.
export function otpAppAddLink(otpAppUrl: string, otpauthUri: string): string {
  const sep = otpAppUrl.includes("?") ? "&" : "?";
  return `${otpAppUrl}${sep}add=${encodeURIComponent(otpauthUri)}`;
}

// 등록·해제 화면의 본문. 셋 중 하나를 그린다 — 등록됨(해제 폼) · 미등록(비밀번호 재확인) ·
// 등록 중(QR + 코드 확인). 바깥 틀(.card·제목)은 부르는 쪽 것이다.
//
// 등록 순서가 이 조각의 요점이다: **코드 확인 전에는 아무것도 저장되지 않는다.** 그
// 보장은 서버의 것이지만, 화면도 그 순서를 사람에게 그대로 보여 준다.
export function TotpEnroll({
  enabled, required, onSetup, onConfirm, onDisable, onEnrolled, onDisabled, confirmDisable,
  appsHint = "Google Authenticator · Microsoft Authenticator",
  otpAppUrl,
  passwordless = false,
  passwordlessNote = "비밀번호가 없는 계정은 최근 로그인으로 본인을 확인합니다. 막히면 다시 로그인한 뒤 해 주세요.",
}: TotpEnrollProps) {
  const [password, setPassword] = useState("");
  const [setup, setSetup] = useState<TotpSetup | null>(null);
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function begin(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setDone(null);
    setBusy(true);
    try {
      setSetup(await onSetup(password));
      setPassword("");
    } catch (err) {
      setError(errorMessage(err, "등록을 시작하지 못했습니다. 다시 시도해 주세요."));
    } finally {
      setBusy(false);
    }
  }

  async function confirm(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await onConfirm(code);
      setSetup(null);
      setCode("");
      setDone("2단계 인증을 등록했습니다. 다음 로그인부터 앱 코드를 묻습니다.");
      onEnrolled?.();
    } catch (err) {
      setError(errorMessage(err, "코드를 확인하지 못했습니다. 앱에 뜬 숫자를 다시 넣어 주세요."));
      setCode("");
    } finally {
      setBusy(false);
    }
  }

  async function disable(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (confirmDisable) {
      setBusy(true);
      let ok = false;
      try {
        ok = await confirmDisable(DISABLE_CONFIRM);
      } finally {
        setBusy(false);
      }
      if (!ok) return;
    }
    setBusy(true);
    try {
      await onDisable(password);
      setPassword("");
      setDone("2단계 인증을 해제했습니다.");
      onDisabled?.();
    } catch (err) {
      setError(errorMessage(err, "해제하지 못했습니다. 다시 시도해 주세요."));
    } finally {
      setBusy(false);
    }
  }

  const errorBox = error && (
    <div className="b-toast b-toast-danger mtotp-toast" role="alert">
      <div className="b-toast-text">
        <div className="b-toast-body">{error}</div>
      </div>
    </div>
  );

  return (
    <div className="mtotp">
      {done && (
        <div className="b-toast b-toast-success mtotp-toast" role="status">
          <div className="b-toast-text">
            <div className="b-toast-body">{done}</div>
          </div>
        </div>
      )}

      {required && !enabled && !setup && (
        <p className="mtotp-note">
          이 계정은 2단계 인증이 <strong>필수</strong>입니다. 인증 앱을 등록해야 계속할 수 있습니다.
        </p>
      )}

      {enabled && !setup && (
        <form onSubmit={disable}>
          <p className="mtotp-note">
            <span className="b-badge b-badge-success">등록됨</span> 로그인할 때 인증 앱의 6자리 코드를 묻습니다.
          </p>
          {required ? (
            <p className="mtotp-note">
              이 계정은 2단계 인증이 필수라 본인이 해제할 수 없습니다. 기기를 잃었으면 관리자에게 해제를 요청해 주세요.
            </p>
          ) : (
            <>
              {passwordless ? (
                <p className="mtotp-note">{passwordlessNote}</p>
              ) : (
                <label className="b-field mtotp-field">
                  <span className="b-field-label mtotp-label">해제하려면 비밀번호</span>
                  <input
                    className="b-input"
                    type="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={busy}
                    required
                  />
                </label>
              )}
              {errorBox}
              <button className="b-btn b-btn-danger b-btn-lg mtotp-submit" type="submit" disabled={busy}>
                {busy ? "해제하는 중…" : "2단계 인증 해제"}
              </button>
            </>
          )}
        </form>
      )}

      {!enabled && !setup && (
        <form onSubmit={begin}>
          <p className="mtotp-note">
            {appsHint} 같은 OTP 앱에 이 계정을 등록합니다. {passwordless ? passwordlessNote : "먼저 비밀번호를 다시 확인합니다."}
          </p>
          {!passwordless && (
            <label className="b-field mtotp-field">
              <span className="b-field-label mtotp-label">비밀번호</span>
              <input
                className="b-input"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={busy}
                autoFocus
                required
              />
            </label>
          )}
          {errorBox}
          <button className="b-btn b-btn-primary b-btn-lg mtotp-submit" type="submit" disabled={busy}>
            {busy ? "준비하는 중…" : "등록 시작"}
          </button>
        </form>
      )}

      {setup && (
        <form onSubmit={confirm}>
          <p className="mtotp-note">
            인증 앱으로 QR 을 스캔하거나 아래 키를 직접 입력한 뒤, 앱에 뜬 6자리 코드를 넣어 주세요. 코드가 맞아야 등록이
            저장됩니다.
          </p>
          <TotpQr uri={setup.uri} />
          <div className="b-field mtotp-field">
            <span className="b-field-label mtotp-label">직접 입력용 키</span>
            <code className="mtotp-secret">{setup.secret}</code>
          </div>
          {otpAppUrl && (
            <p className="mtotp-note">
              휴대폰으로 이 화면을 열었다면 QR 을 찍을 수 없습니다 —{" "}
              <a className="b-btn b-btn-ghost b-btn-sm" href={otpAppAddLink(otpAppUrl, setup.uri)} target="_blank" rel="noopener">
                MOPS OTP 앱에 추가
              </a>{" "}
              를 누르면 같은 기기의 MOPS OTP 앱에 바로 들어갑니다. 그 앱이 낸 코드를 아래에 넣어 주세요.
            </p>
          )}
          <label className="b-field mtotp-field">
            <span className="b-field-label mtotp-label">앱에 표시된 코드</span>
            <TotpCodeInput value={code} onChange={setCode} autoFocus disabled={busy} />
          </label>
          {errorBox}
          <button className="b-btn b-btn-primary b-btn-lg mtotp-submit" type="submit" disabled={busy || code.length !== 6}>
            {busy ? "확인하는 중…" : "코드 확인하고 등록"}
          </button>
          <p className="mtotp-foot">
            <button
              type="button"
              className="b-btn b-btn-ghost b-btn-sm"
              disabled={busy}
              onClick={() => {
                setSetup(null);
                setCode("");
                setError(null);
              }}
            >
              취소
            </button>
          </p>
        </form>
      )}
    </div>
  );
}
